import {
  CasLoginStatus,
  LoginState,
  LoginStatus,
  ProfileTier,
  ProfileType,
} from '@fcom/core-api/login/interfaces/login.interface';
import {
  AccountSecurityActionName,
  AccountSecurityFlow,
  AccountSecurityVerificationCodeFlowStep,
  AccountSecurityFlowType,
  MfaProvider,
  AccountSecurityChangeEmailFlowStep,
  AccountSecurityFlowStep,
  AccountSecurityActionPayload,
} from '@fcom/core-api/login/interfaces/account-security.interface';
import { DataUtils } from '@fcom/core/utils/data-utils';

import * as LoginActions from '../actions/login.actions';
import { SetAccountSecurityFlowActionPayload } from '../actions/login.actions';

const accountSecurityFlowInitialState: AccountSecurityFlow<AccountSecurityFlowType> = Object.seal({
  step: AccountSecurityVerificationCodeFlowStep.Initial,
  lastAction: {
    name: undefined,
    isLoading: false,
    success: false,
  },
  data: { form: { valid: false } },
});

export const initialState: LoginState = Object.seal({
  loginStatus: LoginStatus.INITIAL,
  casLoginStatus: CasLoginStatus.INITIAL,
  accountSecurity: {
    mfa: {
      provider: MfaProvider.None,
      isEnabled: false,
      isInitial: true,
      getStatusError: false,
    },
    lastStandaloneAction: {
      name: undefined,
      isLoading: false,
      success: false,
    },
    flow: Object.values(AccountSecurityFlowType).reduce(
      (acc, flowType) => ({ ...acc, [flowType]: accountSecurityFlowInitialState }),
      {} as Record<AccountSecurityFlowType, AccountSecurityFlow<AccountSecurityFlowType>>
    ),
  },
});

const ACCESS_TOKEN: keyof LoginState = 'accessToken';
const LOGIN_STATUS: keyof LoginState = 'loginStatus';
const CAS_LOGIN_STATUS: keyof LoginState = 'casLoginStatus';
const PROFILE_TYPE: keyof LoginState = 'type';
const USER_PROFILE: keyof LoginState = 'profile';
const ACCOUNT_SECURITY: keyof LoginState = 'accountSecurity';
const CAS_PROFILE: keyof LoginState = 'casProfile';
const LOGIN_PHASE: keyof LoginState = 'loginPhase';

export function loginReducer(state = initialState, action: LoginActions.LoginActionUnion): LoginState {
  switch (action.type) {
    case LoginActions.setAccessToken.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_STATUS, LoginStatus.PENDING)
        .setIn(CAS_LOGIN_STATUS, CasLoginStatus.PENDING)
        .setIn(ACCESS_TOKEN, action.accessToken)
        .value();

    case LoginActions.setProfile.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_STATUS, LoginStatus.LOGGED_IN)
        .setIn(USER_PROFILE, action.profile)
        .setIn(
          PROFILE_TYPE,
          action.profile.tier === ProfileTier.CORPORATE
            ? ProfileType.CORPORATE
            : action.profile.tier === ProfileTier.ACCOUNT
              ? ProfileType.FINNAIR_ACCOUNT
              : ProfileType.FPLUS
        )
        .value();

    case LoginActions.setCasProfile.type:
      return DataUtils.wrap(state)
        .setIn(CAS_LOGIN_STATUS, CasLoginStatus.LOGGED_IN)
        .setIn(CAS_PROFILE, action.casProfile)
        .value();

    case LoginActions.setCasLoginError.type:
      return DataUtils.wrap(state).setIn(CAS_LOGIN_STATUS, CasLoginStatus.ERROR).value();

    case LoginActions.setCasNotLoggedIn.type:
      return DataUtils.wrap(state).setIn(CAS_LOGIN_STATUS, CasLoginStatus.NOT_LOGGED_IN).value();

    case LoginActions.setNotLoggedIn.type:
      return DataUtils.wrap(initialState)
        .setIn(LOGIN_STATUS, LoginStatus.NOT_LOGGED_IN)
        .setIn(LOGIN_PHASE, state.loginPhase)
        .value();

    case LoginActions.setLoginError.type:
      return DataUtils.wrap(initialState).setIn(LOGIN_STATUS, LoginStatus.ERROR).value();

    case LoginActions.setLoginPhaseStep.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_PHASE, {
          ...state.loginPhase,
          step: action.step,
          error: undefined,
        })
        .value();

    case LoginActions.setLoginPhaseExecutionToken.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_PHASE, {
          ...state.loginPhase,
          executionToken: action.token,
        })
        .value();

    case LoginActions.setLoginPhaseProfile.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_PHASE, {
          ...state.loginPhase,
          profile: action.profile,
        })
        .value();

    case LoginActions.setLoginPhaseError.type:
      return DataUtils.wrap(state)
        .setIn(LOGIN_PHASE, {
          ...state.loginPhase,
          error: action.error,
        })
        .value();

    case LoginActions.clearLoginPhase.type:
      return DataUtils.wrap(state).deleteIn(LOGIN_PHASE).value() as LoginState;

    case LoginActions.clearAccessToken.type:
      return initialState;

    case LoginActions.setAccountSecurityStandaloneAction.type:
      return DataUtils.wrap(state)
        .setIn(ACCOUNT_SECURITY, {
          ...state.accountSecurity,
          mfa: (() => {
            const { standaloneAction } = action;

            const getStatusError =
              standaloneAction.name === AccountSecurityActionName.GetMfaStatus && !!standaloneAction.error;

            if (!isAccountSecurityActionSuccessful(standaloneAction)) {
              return { ...state.accountSecurity.mfa, getStatusError };
            }

            let provider: MfaProvider;

            if (standaloneAction.name === AccountSecurityActionName.DisableGAuth) {
              provider = MfaProvider.None;
            } else if (standaloneAction.name === AccountSecurityActionName.GetMfaStatus) {
              provider = standaloneAction.successData?.mfaProvider || MfaProvider.None;
            }

            return {
              ...state.accountSecurity.mfa,
              isEnabled: provider !== MfaProvider.None,
              isInitial: false,
              provider,
              getStatusError,
            };
          })(),
          lastStandaloneAction: {
            ...state.accountSecurity.lastStandaloneAction,
            ...action.standaloneAction,
            success: !action.standaloneAction.isLoading && !action.standaloneAction.error,
            error: action.standaloneAction.error ?? undefined,
            successData: action.standaloneAction.successData ?? undefined,
          },
        })
        .value();

    case LoginActions.setAccountSecurityFlowAction.type:
      return DataUtils.wrap(state)
        .setIn(ACCOUNT_SECURITY, {
          ...state.accountSecurity,
          mfa: (() => {
            const { flowAction } = action;

            if (
              !isAccountSecurityActionSuccessful(flowAction) ||
              !(
                flowAction.name === AccountSecurityActionName.EnableEmailMfa ||
                flowAction.name === AccountSecurityActionName.Validate2faEmailVerificationCode ||
                flowAction.name === AccountSecurityActionName.ValidateGAuth
              )
            ) {
              return state.accountSecurity.mfa;
            }

            const provider =
              flowAction.name === AccountSecurityActionName.EnableEmailMfa
                ? MfaProvider.EmailAuth
                : flowAction.name === AccountSecurityActionName.ValidateGAuth
                  ? MfaProvider.MfaGauth
                  : flowAction.name === AccountSecurityActionName.Validate2faEmailVerificationCode &&
                      flowAction.successData.emailMfaSetToEnabled
                    ? MfaProvider.EmailAuth
                    : state.accountSecurity.mfa.provider;

            return {
              ...state.accountSecurity.mfa,
              isEnabled: true,
              isInitial: false,
              provider,
              getStatusError: false,
            };
          })(),
          flow: {
            ...state.accountSecurity.flow,
            [action.flowType]:
              action.flowAction.name === AccountSecurityActionName.ResetFlow
                ? accountSecurityFlowInitialState
                : {
                    ...state.accountSecurity.flow[action.flowType],
                    step: getAccountSecurityNextFlowStep<typeof action.flowType>(
                      action.flowAction,
                      state.accountSecurity.flow[action.flowType].step
                    ),
                    lastAction: {
                      ...state.accountSecurity.flow[action.flowType].lastAction,
                      ...action.flowAction,
                      success: !action.flowAction.isLoading && !action.flowAction.error,
                      error: action.flowAction.error ?? undefined,
                      result: action.flowAction.successData ?? undefined,
                    },
                    data: (() => {
                      const { flowType, flowAction } = action;

                      if (!isAccountSecurityActionSuccessful(flowAction) || !flowAction.successData) {
                        return state.accountSecurity.flow[flowType].data;
                      }

                      return {
                        ...state.accountSecurity.flow[flowType].data,
                        ...flowAction.successData,
                      };
                    })(),
                  },
          },
        })
        .value();

    case LoginActions.setAccountSecurityFlowData.type:
      return DataUtils.wrap(state)
        .setIn(ACCOUNT_SECURITY, {
          ...state.accountSecurity,
          flow: {
            ...state.accountSecurity.flow,
            [action.flowType]: {
              ...state.accountSecurity.flow[action.flowType],
              data:
                action.flowData === null
                  ? accountSecurityFlowInitialState.data
                  : {
                      ...state.accountSecurity.flow[action.flowType].data,
                      ...action.flowData,
                      form: {
                        ...state.accountSecurity.flow[action.flowType].data.form,
                        ...action.flowData.form,
                      },
                    },
            },
          },
        })
        .value();

    default:
      return state;
  }
}

const isAccountSecurityActionSuccessful = (
  action: AccountSecurityActionPayload<AccountSecurityActionName>
): boolean => {
  return action.name && !action.isLoading && !action.error;
};

const getAccountSecurityNextFlowStep = <T extends AccountSecurityFlowType>(
  flowAction: SetAccountSecurityFlowActionPayload<T>,
  currentStep: AccountSecurityFlowStep<T>
): AccountSecurityFlowStep<T> => {
  if (!isAccountSecurityActionSuccessful(flowAction)) {
    return currentStep;
  }

  switch (flowAction.name) {
    case AccountSecurityActionName.ConfirmEmailChange:
      return AccountSecurityChangeEmailFlowStep.EnterNewEmail as AccountSecurityFlowStep<T>;
    case AccountSecurityActionName.InitiateEmailChange:
    case AccountSecurityActionName.Send2faEmailVerificationCode:
    case AccountSecurityActionName.EnableGAuthValidated:
      return AccountSecurityVerificationCodeFlowStep.VerifyCode as AccountSecurityFlowStep<T>;
    case AccountSecurityActionName.Validate2faEmailVerificationCode:
    case AccountSecurityActionName.ValidateGAuth:
    case AccountSecurityActionName.EnableEmailMfa:
      return AccountSecurityVerificationCodeFlowStep.Completed as AccountSecurityFlowStep<T>;
    default:
      return currentStep;
  }
};
